import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { EventAnnotation } from '../../services/chronologyApi';
import { useDocumentViewer } from '../../contexts/DocumentViewerContext';

interface AnnotatedEventTextProps {
  /** The raw event text (headline or description) to annotate. */
  text: string;
  /** Annotations from the explain API. */
  annotations: EventAnnotation[];
}

interface TextSegment {
  text: string;
  annotation: EventAnnotation | null;
}

const PROVENANCE_META: Record<string, {
  label: string;
  decoration: string;
  restColor: string;
  hoverColor: string;
  badgeClass: string;
}> = {
  verbatim: {
    label: 'Verbatim',
    decoration: 'decoration-solid',
    restColor: 'decoration-emerald-200 dark:decoration-emerald-800/60',
    hoverColor: 'decoration-emerald-500 dark:decoration-emerald-400',
    badgeClass: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  },
  summarised: {
    label: 'Summarised',
    decoration: 'decoration-dashed',
    restColor: 'decoration-amber-200 dark:decoration-amber-800/60',
    hoverColor: 'decoration-amber-500 dark:decoration-amber-400',
    badgeClass: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  },
  synthesised: {
    label: 'Synthesised',
    decoration: 'decoration-dotted',
    restColor: 'decoration-indigo-300 dark:decoration-indigo-700/60',
    hoverColor: 'decoration-indigo-500 dark:decoration-indigo-400',
    badgeClass: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
  },
};

/**
 * Renders event text with inline provenance annotations.
 * Each annotated span gets an underline (solid/dashed/dotted by provenance type)
 * and a hover tooltip showing the source.
 */
export const AnnotatedEventText: React.FC<AnnotatedEventTextProps> = ({
  text,
  annotations,
}) => {
  const { openDocument } = useDocumentViewer();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number } | null>(null);
  const activeSpanRef = useRef<HTMLSpanElement | null>(null);

  const TOOLTIP_WIDTH = 288; // w-72 = 18rem = 288px

  const updateTooltipPos = useCallback((spanEl: HTMLSpanElement | null) => {
    if (!spanEl) { setTooltipPos(null); return; }
    const rect = spanEl.getBoundingClientRect();
    const viewportW = window.innerWidth;

    // Position above the span by default
    let left = rect.left;
    const top = rect.top - 8; // 8px gap above

    // Clamp horizontally so tooltip doesn't overflow viewport
    if (left + TOOLTIP_WIDTH > viewportW - 16) {
      left = viewportW - TOOLTIP_WIDTH - 16;
    }
    if (left < 16) left = 16;

    setTooltipPos({ top, left });
  }, []);

  // Build non-overlapping segments by matching annotation text spans to the source text
  const segments = useMemo<TextSegment[]>(() => {
    if (!annotations.length) return [{ text, annotation: null }];

    // Find positions of each annotation in the text
    const matches: Array<{ start: number; end: number; annotation: EventAnnotation }> = [];
    const usedRanges: Array<{ start: number; end: number }> = [];

    for (const ann of annotations) {
      if (!ann.text) continue;

      // Try exact match first
      let idx = findNonOverlapping(text, ann.text, usedRanges);

      // Fallback: try matching without citation markers [1], [2] etc.
      if (idx === -1) {
        const cleanAnn = ann.text.replace(/\[\d+\]/g, '').trim();
        const cleanText = text.replace(/\[\d+\]/g, '');
        const cleanIdx = findNonOverlapping(cleanText, cleanAnn, []);
        if (cleanIdx !== -1) {
          idx = mapCleanIndexToOriginal(text, cleanIdx, cleanAnn.length);
        }
      }

      // Fallback: strip markdown bold markers (**) and citation markers
      if (idx === -1) {
        const stripAll = (s: string) => s.replace(/\*\*/g, '').replace(/\[\d+\]/g, '').trim();
        const cleanAnn = stripAll(ann.text);
        const cleanText = text.replace(/\*\*/g, '').replace(/\[\d+\]/g, '');
        const cleanIdx = findNonOverlapping(cleanText, cleanAnn, []);
        if (cleanIdx !== -1) {
          idx = mapCleanIndexToOriginal(text, cleanIdx, cleanAnn.length);
        }
      }

      if (idx !== -1) {
        const end = idx + findOriginalLength(text, idx, ann.text);
        matches.push({ start: idx, end, annotation: ann });
        usedRanges.push({ start: idx, end });
      }
    }

    // Sort by position
    matches.sort((a, b) => a.start - b.start);

    // Build segments
    const result: TextSegment[] = [];
    let cursor = 0;

    for (const match of matches) {
      if (match.start > cursor) {
        result.push({ text: text.slice(cursor, match.start), annotation: null });
      }
      result.push({ text: text.slice(match.start, match.end), annotation: match.annotation });
      cursor = match.end;
    }

    if (cursor < text.length) {
      result.push({ text: text.slice(cursor), annotation: null });
    }

    return result;
  }, [text, annotations]);

  const handleOpenDocument = (ann: EventAnnotation, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!ann.documentId || !ann.documentName) return;
    openDocument({
      documentId: ann.documentId,
      documentName: ann.documentName,
      pageNumber: ann.pageNumber ?? undefined,
    });
  };

  // Render inline text with **bold**, \n\n paragraph breaks, and [N] citation markers stripped
  const renderFormattedText = (raw: string): React.ReactNode[] => {
    // Strip citation markers and PII tags
    const cleaned = raw.replace(/\s*\[\d+\]/g, '').replace(/\{\{PII:[A-Z_]+\}\}/g, '').replace(/\{\{\/PII\}\}/g, '');
    const nodes: React.ReactNode[] = [];
    // Split on \n\n for paragraph breaks
    const paragraphs = cleaned.split(/\n\n/);
    paragraphs.forEach((para, pi) => {
      if (pi > 0) nodes.push(<span key={`br-${pi}`} className="block mt-2" />);
      // Split on **...** for bold sections
      const parts = para.split(/(\*\*[^*]+\*\*)/);
      parts.forEach((part, partIdx) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          nodes.push(<strong key={`b-${pi}-${partIdx}`}>{part.slice(2, -2)}</strong>);
        } else if (part) {
          nodes.push(<React.Fragment key={`t-${pi}-${partIdx}`}>{part}</React.Fragment>);
        }
      });
    });
    return nodes;
  };

  return (
    <span className="annotated-event-text">
      {segments.map((seg, i) => {
        if (!seg.annotation) {
          return <span key={i}>{renderFormattedText(seg.text)}</span>;
        }

        const ann = seg.annotation;
        const provenance = ann.provenance || 'summarised';
        const meta = PROVENANCE_META[provenance] || PROVENANCE_META.summarised;
        const isActive = activeIndex === i;

        return (
          <span
            key={i}
            className="inline"
            onMouseEnter={(e) => {
              setActiveIndex(i);
              activeSpanRef.current = e.currentTarget as HTMLSpanElement;
              updateTooltipPos(e.currentTarget as HTMLSpanElement);
            }}
            onMouseLeave={() => { setActiveIndex(null); setTooltipPos(null); }}
          >
            {/* Annotated text — click opens source document */}
            <span
              onClick={(e) => { if (ann.documentId && ann.documentName) handleOpenDocument(ann, e); }}
              className={`underline underline-offset-4 decoration-[1.5px] ${meta.decoration} ${ann.documentId ? 'cursor-pointer' : 'cursor-default'} transition-all duration-150 ${
                isActive ? meta.hoverColor : meta.restColor
              }`}
            >
              {renderFormattedText(seg.text)}
            </span>

            {/* Tooltip — rendered via portal to avoid overflow clipping */}
            {isActive && tooltipPos && createPortal(
              <div
                className="fixed z-[9999] pointer-events-auto"
                style={{ top: tooltipPos.top, left: tooltipPos.left, transform: 'translateY(-100%)' }}
                onMouseEnter={() => setActiveIndex(i)}
                onMouseLeave={() => { setActiveIndex(null); setTooltipPos(null); }}
              >
                <div className="w-72 px-3 py-2.5 rounded-lg bg-white dark:bg-medical-800 border border-medical-200 dark:border-medical-600 shadow-lg text-xs leading-relaxed">
                  {/* Provenance badge + source label */}
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide shrink-0 ${meta.badgeClass}`}>
                      {meta.label}
                    </span>
                    <span className="text-medical-800 dark:text-medical-200 font-medium truncate">
                      {ann.sourceLabel || 'Unknown source'}
                    </span>
                  </div>

                  {/* Source description */}
                  {ann.sourceDescription && (
                    <div className="text-medical-500 dark:text-medical-400 mt-1">
                      {ann.sourceDescription}
                    </div>
                  )}
                </div>
              </div>,
              document.body,
            )}
          </span>
        );
      })}
    </span>
  );
};

/** Find index of `needle` in `haystack` that doesn't overlap with any used range. */
function findNonOverlapping(
  haystack: string,
  needle: string,
  usedRanges: Array<{ start: number; end: number }>,
): number {
  let searchFrom = 0;
  while (true) {
    const idx = haystack.indexOf(needle, searchFrom);
    if (idx === -1) return -1;

    const end = idx + needle.length;
    const overlaps = usedRanges.some(
      (r) => idx < r.end && end > r.start,
    );

    if (!overlaps) return idx;
    searchFrom = idx + 1;
  }
}

/**
 * When matching a cleaned (no markers) string back to the original,
 * find the original-text index corresponding to `cleanIdx`.
 * Skips citation markers [N] and markdown bold markers **.
 */
function mapCleanIndexToOriginal(original: string, cleanIdx: number, _cleanLen: number): number {
  let oi = 0;
  let ci = 0;

  while (oi < original.length && ci < cleanIdx) {
    // Skip citation markers [1], [2], etc.
    const citationMatch = original.slice(oi).match(/^\[\d+\]/);
    if (citationMatch) {
      oi += citationMatch[0].length;
      continue;
    }
    // Skip markdown bold markers **
    if (original.slice(oi, oi + 2) === '**') {
      oi += 2;
      continue;
    }
    oi++;
    ci++;
  }

  return oi;
}

/**
 * Find the length in the original text that corresponds to the annotation text,
 * accounting for possible citation markers and markdown bold markers.
 */
function findOriginalLength(original: string, startIdx: number, annText: string): number {
  // If the annotation text appears exactly, use its length
  if (original.slice(startIdx, startIdx + annText.length) === annText) {
    return annText.length;
  }

  // Otherwise walk through both strings, skipping markers in original
  const cleanAnn = annText.replace(/\[\d+\]/g, '').replace(/\*\*/g, '');
  let oi = startIdx;
  let matched = 0;

  while (oi < original.length && matched < cleanAnn.length) {
    // Skip citation markers
    const citationMatch = original.slice(oi).match(/^\[\d+\]/);
    if (citationMatch) {
      oi += citationMatch[0].length;
      continue;
    }
    // Skip markdown bold markers
    if (original.slice(oi, oi + 2) === '**') {
      oi += 2;
      continue;
    }
    if (original[oi] === cleanAnn[matched]) {
      matched++;
    }
    oi++;
  }

  return oi - startIdx;
}
